/* New cart drawer interactions (Figma 1:1). Scoped to [data-cart-drawer] and to
   v2-namespaced data-attributes only, so it never collides with the old
   drawer's document-level handlers in cart-drawer-scripts.liquid /
   cart-line-items-scripts.liquid. Quantity changes POST to /cart/change.js
   directly and re-sync via window.sprklRefreshCartDrawer() (cart-drawer-scripts.liquid) -
   NOT window.cart.updateCart()/getCart(): that legacy pair repaints
   [data-cart-price-holder] from the old api-cart-items section's plain markup,
   which has no v2 savings/gift/shipping-protection classes and wipes them
   until a full page reload. */
(function () {
  if (window.__cartDrawerV2Registered) return;
  window.__cartDrawerV2Registered = true;

  // Shared tracer defined in cart-drawer-scripts.liquid; no-op if that snippet
  // hasn't run yet or debug is off. See "[SPRKL_TRACE]" console filter.
  function trace(fn, why, data) {
    if (typeof window.sprklTrace === 'function') window.sprklTrace(fn, why, data);
  }
  function withInit(initiator, fn, why, body) {
    if (typeof window.sprklWithInitiator === 'function') return window.sprklWithInitiator(initiator, fn, why, body);
    return body();
  }

  // key: line_item.key -> { timeoutId, itemEl, card }
  var undoTimers = new Map();

  function drawerRoot(el) {
    return el.closest('[data-cart-drawer]');
  }

  function postCartChange(id, quantity) {
    return fetch('/cart/change.js', {
      method: 'post',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({ id: id, quantity: quantity }),
    })
      .then(function (r) { return r.json(); })
      .then(function (r) {
        if (r.status) throw new Error(r.message || 'cart error');
        return r;
      });
  }

  function updateLine(id, quantity, itemEl) {
    if (itemEl) {
      if (itemEl.classList.contains('is-loading')) return;
      itemEl.classList.add('is-loading');
    }
    var drawer = (itemEl && drawerRoot(itemEl)) || document.querySelector('[data-cart-drawer]');
    trace('cartDrawerV2.updateLine', 'USER changed quantity via +/- buttons -> POST /cart/change.js', {
      key: id, new_quantity: quantity,
    });

    return withInit('user-qty-change', 'cartDrawerV2.updateLine', 'quantity change', function () {
    return postCartChange(id, quantity)
      .then(function () {
        if (typeof window.sprklRefreshCartDrawer === 'function') {
          // skipLoadingShell: the row already shows its own spinner (.is-loading above); swapping
          // the whole item list to a generic skeleton on top of that would just be a second, jumpier
          // loading state for the same change.
          return window.sprklRefreshCartDrawer(drawer, { skipLoadingShell: true, reason: 'user-qty-change' });
        }
        if (window.cart && typeof window.cart.getCart === 'function') window.cart.getCart();
      })
      .catch(function () {
        trace('cartDrawerV2.updateLine', 'quantity change FAILED -> fallback window.cart.getCart()', { key: id });
        if (window.cart && typeof window.cart.getCart === 'function') window.cart.getCart();
      })
      .finally(function () {
        if (itemEl) itemEl.classList.remove('is-loading');
      });
    });
  }

  // Only one remove/undo flow may be in flight at a time: finalizeRemoval() reads and
  // writes the cart via two sequential fetches, and a second one started in that window
  // would race it (only the first survives). Lock every other row's quantity buttons for
  // the duration instead of letting a second one silently no-op.
  function setOtherItemsQtyLocked(holder, activeItemEl, locked) {
    if (!holder) return;
    holder.querySelectorAll('[data-v2-cart-item]').forEach(function (el) {
      if (el !== activeItemEl) el.classList.toggle('cart-drawer-v2-item--qty-locked', locked);
    });
  }

  function startRemoveFlow(itemEl, key) {
    if (undoTimers.has(key)) return;
    trace('cartDrawerV2.startRemoveFlow', 'USER pressed minus at qty 1 -> show 3s undo, defer actual removal', { key: key });

    var holder = itemEl.closest('[data-items-holder]');
    var tpl = holder && holder.querySelector('[data-v2-removed-card-template]');
    if (!tpl) return;

    setOtherItemsQtyLocked(holder, itemEl, true);

    var card = tpl.content.firstElementChild.cloneNode(true);
    itemEl.classList.add('cart-drawer-v2-item--removing');
    var qtyField = itemEl.querySelector('[data-v2-quantity-field]');
    if (qtyField) qtyField.value = 0;
    itemEl.insertAdjacentElement('afterend', card);

    requestAnimationFrame(function () {
      var fill = card.querySelector('[data-v2-undo-progress]');
      if (fill) fill.classList.add('is-counting-down');
    });

    var timeoutId = window.setTimeout(function () {
      finalizeRemoval(key);
    }, 3000);

    undoTimers.set(key, { timeoutId: timeoutId, itemEl: itemEl, card: card });

    var undoBtn = card.querySelector('[data-v2-undo-btn]');
    if (undoBtn) {
      undoBtn.addEventListener('click', function () {
        cancelRemoveFlow(key);
      });
    }
  }

  function cancelRemoveFlow(key) {
    var entry = undoTimers.get(key);
    if (!entry) return;
    trace('cartDrawerV2.cancelRemoveFlow', 'USER clicked UNDO -> cancel pending removal (no cart mutation)', { key: key });
    window.clearTimeout(entry.timeoutId);
    undoTimers.delete(key);
    entry.card.remove();
    entry.itemEl.classList.remove('cart-drawer-v2-item--removing');
    var qtyField = entry.itemEl.querySelector('[data-v2-quantity-field]');
    if (qtyField) qtyField.value = 1;
    if (!undoTimers.size) setOtherItemsQtyLocked(entry.itemEl.closest('[data-items-holder]'), null, false);
  }

  async function finalizeRemoval(key) {
    var entry = undoTimers.get(key);
    undoTimers.delete(key);
    var itemEl = entry ? entry.itemEl : null;
    if (itemEl) itemEl.classList.add('is-loading');
    var drawer = (itemEl && drawerRoot(itemEl)) || document.querySelector('[data-cart-drawer]');
    trace('cartDrawerV2.finalizeRemoval', 'undo window elapsed (3s) -> committing removal + cascade of gift/upsell children', { key: key });
    return withInit('v2-remove-finalize', 'cartDrawerV2.finalizeRemoval', 'finalize deferred removal', function () {
      return finalizeRemovalInner(key, itemEl, drawer);
    });
  }

  // Remove `key`, plus any upsell line(s) added against it (properties[_upsell_parent] === key,
  // set by cart-upsell-item-card.liquid) and any metafield-driven gift line for the same PRODUCT
  // (properties[_gift_parent_product], added by reconcileGiftProductsCore() in
  // cart-drawer-scripts.liquid) - but only the gift if no other cart line for that product remains,
  // since a single-purchase line and a subscription line of the same product share ONE gift set -
  // in the same /cart/update.js call so the child never outlives its parent. Shared by the undo-window
  // removal flow (finalizeRemovalInner) and the upsell "Upgrade" flow (which also removes a line).
  async function removeLineCascade(key) {
    try {
      var updates = {};
      updates[key] = 0;
      try {
        var cart = await fetch('/cart.js').then(function (r) { return r.json(); });
        var removedItem = cart.items.find(function (item) { return item.key === key; });
        var otherLineForSameProduct = removedItem && window.SPRKL.hasOtherLineForSameProduct(cart, removedItem);
        cart.items.forEach(function (item) {
          if (!item.properties) return;
          if (item.properties._upsell_parent === key) {
            updates[item.key] = 0;
          } else if (
            removedItem &&
            !otherLineForSameProduct &&
            String(item.properties._gift_parent_product || item.properties._gift_parent) === String(removedItem.product_id)
          ) {
            updates[item.key] = 0;
          }
        });
      } catch (e) {}
      await fetch('/cart/update.js', {
        method: 'post',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({ updates: updates }),
      }).then(function (r) { return r.json(); });

      // Re-check *after* our own removal committed (not before - two removals
      // finalizing within the same undo window would otherwise both read a stale
      // "other item still paid" snapshot and neither would clean up, orphaning the
      // free gift forever). A $0 line only makes sense alongside a paid one.
      try {
        var cartAfter = await fetch('/cart.js').then(function (r) { return r.json(); });
        var hasPaid = cartAfter.items.some(function (item) { return (item.final_line_price || item.line_price || 0) > 0; });
        if (!hasPaid && cartAfter.items.length) {
          var cleanupUpdates = {};
          cartAfter.items.forEach(function (item) {
            if ((item.final_line_price || item.line_price || 0) === 0) cleanupUpdates[item.key] = 0;
          });
          await fetch('/cart/update.js', {
            method: 'post',
            headers: {'Content-Type': 'application/json', Accept: 'application/json'},
            body: JSON.stringify({ updates: cleanupUpdates }),
          });
        }
      } catch (e) {}
    } catch (e) {}
  }

  async function finalizeRemovalInner(key, itemEl, drawer) {
    await removeLineCascade(key);

    try {
      if (typeof window.sprklRefreshCartDrawer === 'function') {
        trace('cartDrawerV2.finalizeRemoval', 'removal committed -> refresh drawer', { key: key });
        await window.sprklRefreshCartDrawer(drawer, { skipLoadingShell: true, reason: 'v2-remove-finalize' });
      } else if (window.cart && typeof window.cart.getCart === 'function') {
        window.cart.getCart();
      }
    } finally {
      if (itemEl) itemEl.classList.remove('is-loading');
      // Normally moot: refreshCartDrawer() wholesale-replaces the holder, so the locked
      // rows are gone along with their class. Kept as a fallback for the no-refresh-fn path.
      if (!undoTimers.size) setOtherItemsQtyLocked(drawer, null, false);
    }
  }

  // "Complete your routine" recommendation add: fully owned here instead of letting
  // theme.dev.js's global [data-add-to-cart] click handler (cartAddEvent() -> addToCart() ->
  // getCart() -> build(), theme.dev.js) run. That handler unconditionally dispatches its own
  // 'theme:cart:added' from inside build(), racing our v2 footer-repair refresh
  // (window.sprklRefreshCartDrawer, below) - both listen for the same event and either can
  // win, so data-v2-gift-pairs on [data-items-holder] (read by cart-drawer-scripts.liquid's
  // gift-reconcile) isn't reliably updated with the just-added product before reconcile reads
  // it. Same fix already applied to the upsell-card Upgrade button below: take the click,
  // add ourselves, refresh ourselves, THEN dispatch 'theme:cart:added' - so every listener on
  // it (gift-reconcile, header count, recommendations refresh) sees a fully-settled cart.
  // Also covers the Appstle Subscriptions widget's window.fetch monkey-patch (see bootstrap.liquid
  // getCleanFetch() comment): it force-attaches whatever selling_plan checkbox is currently
  // checked anywhere on the page onto ANY /cart/add(.js) call it observes, which was silently
  // turning a plain recommendation add into a subscription for a variant not even enrolled in
  // that plan - routing through getCleanFetch() (iframe-borrowed, unpatched) avoids that too.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.cart-drawer-v2-routine-item [data-add-to-cart]');
    if (!btn) return;
    if (btn.classList.contains('is-loading')) return;

    e.preventDefault();
    e.stopPropagation();

    var form = btn.closest('form');
    var variantInput = form && form.querySelector('input[name="id"]');
    var variantId = variantInput && variantInput.value;
    if (!variantId) return;

    btn.classList.add('is-loading');
    btn.disabled = true;

    var addFetch = (window.SPRKL && typeof window.SPRKL.getCleanFetch === 'function')
      ? window.SPRKL.getCleanFetch()
      : fetch;

    addFetch('/cart/add.js', {
      method: 'post',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    })
      .then(function (r) { return r.json(); })
      .then(function (r) {
        if (r.status) throw new Error(r.message || 'cart error');
        var drawer = drawerRoot(btn) || document.querySelector('[data-cart-drawer]');
        var refreshed = typeof window.sprklRefreshCartDrawer === 'function'
          ? window.sprklRefreshCartDrawer(drawer, { skipLoadingShell: true })
          : (window.cart && typeof window.cart.getCart === 'function' ? window.cart.getCart() : null);
        return Promise.resolve(refreshed).then(function () {
          document.dispatchEvent(new CustomEvent('theme:cart:added', { bubbles: true }));
        });
      })
      .catch(function () {})
      .finally(function () {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      });
  }, true);

  // "Complete your routine" upsell in replace mode (settings.cart_upsell_replaces_item,
  // mirrored as data-v2-upsell-replace on [data-items-holder]): UPGRADE removes the line
  // item the card was recommended under and adds the upsell variant in its place, instead
  // of adding it alongside, keeping it on subscription (under the plan the card was priced
  // with) so upgrading a subscribed item doesn't silently drop it back to one-time. Captured
  // ahead of theme.dev.js's own bubble-phase add-to-cart handler (registered plain, non-
  // capturing) and stopped here so that handler never also fires for this button. Sequenced
  // add-THEN-remove (reversed from the old remove-then-add): if the add fails - eg. the plan
  // was deleted between render and click - the parent line must still be
  // there, since silently losing the customer's item on a failed add is worse than the cart
  // briefly holding both lines server-side (the drawer itself never repaints until both
  // steps finish, so the user never sees an overlap either way).
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.cart-drawer-v2-upsell-card [data-add-to-cart]');
    if (!btn) return;
    var holder = document.querySelector('[data-items-holder]');
    if (!holder || holder.getAttribute('data-v2-upsell-replace') !== 'true') return;

    e.preventDefault();
    e.stopPropagation();
    if (btn.classList.contains('is-loading')) return;

    var form = btn.closest('form');
    var variantInput = form && form.querySelector('input[name="id"]');
    var parentInput = form && form.querySelector('input[name="properties[_upsell_parent]"]');
    var variantId = variantInput && variantInput.value;
    var parentKey = parentInput && parentInput.value;
    // Plan the card was priced under (cart-upsell-item-card.liquid): one of THIS product's
    // own plans, picked closest in delivery interval to the parent line's. Absent on a
    // one-time upgrade, and on a target that sells no subscription at all - in both cases
    // the add is deliberately one-time. Never replay the parent line's plan id instead:
    // plans are per-product, so that id usually doesn't exist here and the add would fail.
    var planInput = form && form.querySelector('input[name="selling_plan"]');
    var cardSellingPlan = planInput && planInput.value;
    if (!variantId) return;

    btn.classList.add('is-loading');
    btn.disabled = true;

    // Same iframe-borrowed fetch used for gift-with-purchase adds (bootstrap.liquid
    // getCleanFetch()): the Appstle Subscriptions widget monkey-patches window.fetch to
    // force-attach whatever selling_plan checkbox is currently checked anywhere on the page
    // onto ANY /cart/add(.js) call it observes. Without this, that hijack could stomp the
    // selling_plan we deliberately set below (or add a subscription the customer never opted
    // into on a plain one-time upgrade).
    var addFetch = (window.SPRKL && typeof window.SPRKL.getCleanFetch === 'function')
      ? window.SPRKL.getCleanFetch()
      : fetch;

    // Failure to read the parent line back just means we skip carrying its selling_plan/qty
    // forward - it must never abort the whole upgrade, so this leg always resolves.
    (parentKey
      ? fetch('/cart.js').then(function (r) { return r.json(); }).catch(function () { return null; })
      : Promise.resolve(null))
      .then(function (cart) {
        var parentItem = cart && cart.items.find(function (item) { return item.key === parentKey; });
        var payload = { id: variantId, quantity: (parentItem && parentItem.quantity) || 1 };
        if (cardSellingPlan) payload.selling_plan = cardSellingPlan;
        return addFetch('/cart/add.js', {
          method: 'post',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({ items: [payload] }),
        });
      })
      .then(function (r) { return r.json(); })
      .then(function (r) {
        if (r.status) throw new Error(r.message || 'cart error');
        return parentKey ? removeLineCascade(parentKey) : Promise.resolve();
      })
      .then(function () {
        var drawer = drawerRoot(btn) || document.querySelector('[data-cart-drawer]');
        var refreshed = typeof window.sprklRefreshCartDrawer === 'function'
          ? window.sprklRefreshCartDrawer(drawer, { skipLoadingShell: true })
          : (window.cart && typeof window.cart.getCart === 'function' ? window.cart.getCart() : null);
        // Fire only AFTER the refresh above lands, not right after the /cart/add.js response:
        // getGiftPairs() (cart-drawer-scripts.liquid) reads data-v2-gift-pairs off [data-items-holder],
        // a liquid attribute baked from the cart state as of the last drawer render - dispatching
        // any earlier would arm the gift-reconcile trio (armGiftTrio -> scheduleGiftProductReconcile,
        // its normal 'theme:cart:added' signal - this add went via fetch() instead of that flow, so
        // it never fired on its own) against pairs that don't include the upgraded-TO product yet,
        // reconcile would find nothing to add, and there's no second chance: the refresh's own
        // cart:updated is deliberately suppressed (sprklDispatchingRefreshUpdate) to avoid re-arming
        // the trio in a loop. Once the refresh is in, the pairs are current and reconcile can find
        // the upgraded-TO product's gift-with-purchase, if any, and add it when in stock.
        return Promise.resolve(refreshed).then(function () {
          document.dispatchEvent(new CustomEvent('theme:cart:added', { bubbles: true }));
        });
      })
      .catch(function () {})
      .finally(function () {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      });
  }, true);

  // Subscription upgrade: swap the current line item's selling_plan to the
  // maximum (longest) plan.  The card is rendered by cart-subscription-upgrade-card.liquid
  // and carries data-v2-sub-upgrade-btn with the target selling_plan id.
  // Sequenced remove-THEN-add (reversed from the product-replace upsell above) because
  // we're re-adding the SAME variant - having it in the cart twice briefly is fine,
  // but Shopify would merge quantities if we added first, then removed would subtract,
  // leaving the customer with qty 0.  Actually: we add with the NEW selling_plan first,
  // then remove the OLD line (different selling_plan = different line key, so Shopify
  // won't merge them).  Same add-then-remove order as the product upsell above.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-v2-sub-upgrade-btn]');
    if (!btn) return;
    var card = btn.closest('[data-v2-sub-upgrade-card]');
    if (!card || !drawerRoot(card)) return;

    e.preventDefault();
    e.stopPropagation();
    if (btn.classList.contains('is-loading')) return;

    var lineKey = card.getAttribute('data-line-key');
    var variantId = card.getAttribute('data-variant-id');
    var targetSellingPlan = card.getAttribute('data-target-selling-plan');
    if (!variantId || !targetSellingPlan) return;

    btn.classList.add('is-loading');
    btn.disabled = true;

    var addFetch = (window.SPRKL && typeof window.SPRKL.getCleanFetch === 'function')
      ? window.SPRKL.getCleanFetch()
      : fetch;

    // Read current cart to get the line item's quantity (carry it forward).
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .catch(function () { return null; })
      .then(function (cart) {
        var currentItem = cart && cart.items.find(function (item) { return item.key === lineKey; });
        var qty = (currentItem && currentItem.quantity) || 1;

        // 1. Add same variant with the new (maximum) selling_plan.
        return addFetch('/cart/add.js', {
          method: 'post',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({ items: [{ id: variantId, quantity: qty, selling_plan: targetSellingPlan }] }),
        })
          .then(function (r) { return r.json(); })
          .then(function (r) {
            if (r.status) throw new Error(r.message || 'cart error');
            // 2. Remove old line (different selling_plan = different line key, safe).
            return lineKey ? removeLineCascade(lineKey) : Promise.resolve();
          });
      })
      .then(function () {
        var drawer = drawerRoot(card) || document.querySelector('[data-cart-drawer]');
        var refreshed = typeof window.sprklRefreshCartDrawer === 'function'
          ? window.sprklRefreshCartDrawer(drawer, { skipLoadingShell: true })
          : (window.cart && typeof window.cart.getCart === 'function' ? window.cart.getCart() : null);
        return Promise.resolve(refreshed).then(function () {
          document.dispatchEvent(new CustomEvent('theme:cart:added', { bubbles: true }));
        });
      })
      .catch(function () {})
      .finally(function () {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      });
  }, true);

  document.addEventListener('click', function (e) {
    var minusBtn = e.target.closest('[data-v2-quantity-minus]');
    var plusBtn = e.target.closest('[data-v2-quantity-plus]');
    var btn = minusBtn || plusBtn;
    if (!btn || !drawerRoot(btn)) return;

    var itemEl = btn.closest('[data-v2-cart-item]');
    if (!itemEl || itemEl.classList.contains('is-loading') || itemEl.classList.contains('cart-drawer-v2-item--qty-locked')) return;

    var key = itemEl.getAttribute('data-v2-item-key');
    var qtyField = itemEl.querySelector('[data-v2-quantity-field]');
    var current = parseInt((qtyField && qtyField.value) || itemEl.getAttribute('data-v2-item-quantity'), 10) || 1;
    var max = parseInt(itemEl.getAttribute('data-v2-item-max'), 10);
    var hasLimit = itemEl.getAttribute('data-v2-item-has-limit') === 'true';

    if (minusBtn) {
      if (current <= 1) {
        startRemoveFlow(itemEl, key);
        return;
      }
      if (qtyField) qtyField.value = current - 1;
      updateLine(key, current - 1, itemEl);
      return;
    }

    if (plusBtn) {
      if (hasLimit && !isNaN(max) && current >= max) return;
      if (qtyField) qtyField.value = current + 1;
      updateLine(key, current + 1, itemEl);
    }
  }, true);

  document.addEventListener('change', function (e) {
    var toggle = e.target.closest('[data-v2-shipping-protection-toggle]');
    if (!toggle || !drawerRoot(toggle)) return;

    // updateLine()/window.cart.updateCart() only works on line items already
    // present in the DOM (it looks up an existing [data-item] node to resolve
    // a /cart/change.js line index) - useless for adding this variant for the
    // first time, so this addon talks to the cart endpoints directly instead.
    var variantId = toggle.getAttribute('data-variant-id');
    var checked = toggle.checked;
    toggle.disabled = true;
    trace('cartDrawerV2.shippingProtectionToggle', 'USER toggled shipping protection -> ' + (checked ? 'add' : 'remove') + ' addon line', {
      variant_id: variantId, checked: checked,
    });

    var request = checked
      ? fetch('/cart/add.js', {
          method: 'post',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        })
      : fetch('/cart/change.js', {
          method: 'post',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({ id: variantId, quantity: 0 }),
        });

    request
      .then(function (r) { return r.json(); })
      .then(function (r) {
        if (r.status) throw new Error(r.message || 'cart error');
        // Not window.cart.getCart(): that repaints [data-cart-price-holder] from the legacy
        // api-cart-items section, wiping the v2 shipping-protection/gift-savings markup until
        // a full page reload. refreshCartDrawer() re-renders the actual page and keeps it.
        // footOnly (implies skipLoadingShell): this toggle only ever changes [data-foot-holder]
        // (addon row, savings, checkout total) - the protection product is filtered out of the
        // rendered item list (cart-line-items-new.liquid) and never affects the gift orphan
        // check, so re-rendering the whole drawer for it was both a pointless flash and an
        // unnecessary extra /cart.js round trip.
        if (typeof window.sprklRefreshCartDrawer === 'function') {
          window.sprklRefreshCartDrawer(drawerRoot(toggle), { skipLoadingShell: true, footOnly: true, reason: 'shipping-protection-toggle' });
        } else if (window.cart && typeof window.cart.getCart === 'function') {
          window.cart.getCart();
        }
      })
      .catch(function () {
        toggle.checked = !checked;
      })
      .finally(function () {
        toggle.disabled = false;
      });
  });

  // --- "Complete your routine" recommendations mode: server-render modes
  // (collection / product_list) need no JS. Recommendations mode fetches the
  // dedicated cart-routine-recommendations section client-side.
  // `cart` is optional (pass an already-fetched /cart.js payload to avoid a refetch).
  // Mirrors cart-routine-product-card.liquid's real markup 1:1 (same
  // cart-drawer-v2-routine-item__image / __info classes) so the skeleton occupies
  // exactly the same box as the loaded row instead of just approximating it.
  var ROUTINE_SKELETON_ROW =
    '<div class="cart-drawer-v2-routine-item cart-drawer-v2-routine-item--skeleton">' +
      '<div class="cart-drawer-v2-routine-item__image cart-drawer-v2-routine-skeleton__image"></div>' +
      '<div class="cart-drawer-v2-routine-item__info">' +
        '<span class="cart-drawer-v2-skeleton-bar cart-drawer-v2-skeleton-bar--title"></span>' +
        '<span class="cart-drawer-v2-skeleton-bar cart-drawer-v2-skeleton-bar--sm cart-drawer-v2-skeleton-bar--short"></span>' +
        '<span class="cart-drawer-v2-routine-skeleton__add"></span>' +
      '</div>' +
    '</div>';
  var ROUTINE_SKELETON_HTML = ROUTINE_SKELETON_ROW + ROUTINE_SKELETON_ROW;

  // Extras holder (routine + promo banner) has no content of its own worth showing
  // once routine is hidden and no promo banner is configured. Cart-empty (data-items-holder
  // hidden) already forces extras hidden via refreshExtras and takes priority over this.
  function updateExtrasVisibility() {
    var itemsHolder = document.querySelector('[data-items-holder]');
    var extras = document.querySelector('[data-v2-extras-holder]');
    if (!extras || (itemsHolder && itemsHolder.classList.contains('hidden'))) return;
    var promo = extras.querySelector('.cart-drawer-v2-promo');
    var routineWrap = extras.querySelector('[data-v2-routine-wrap]');
    var hasContent = !!promo || (routineWrap && !routineWrap.classList.contains('hidden'));
    extras.classList.toggle('hidden', !hasContent);
  }

  function setRoutineWrapVisible(holder, visible) {
    var wrap = holder.closest('[data-v2-routine-wrap]');
    if (wrap) wrap.classList.toggle('hidden', !visible);
    updateExtrasVisibility();
  }

  function loadRecommendations(holder, cart) {
    if (holder.dataset.v2Loading === 'true') return;
    trace('cartDrawerV2.loadRecommendations', 'fetching "complete your routine" recommendations section', { reused_cart: !!cart });
    holder.dataset.v2Loading = 'true';
    holder.innerHTML = ROUTINE_SKELETON_HTML;
    setRoutineWrapVisible(holder, true);

    (cart ? Promise.resolve(cart) : fetch('/cart.js').then(function (r) { return r.json(); }))
      .then(function (cart) {
        if (!cart.items.length) return '';
        var productId = cart.items[cart.items.length - 1].product_id;
        var limit = holder.getAttribute('data-limit') || 4;
        var url = window.theme.routes.product_recommendations_url +
          '?section_id=cart-routine-recommendations&limit=' + limit + '&product_id=' + productId;
        return fetch(url).then(function (r) { return r.text(); });
      })
      .then(function (html) {
        var el = document.createElement('div');
        el.innerHTML = html || '';
        holder.innerHTML = el.innerHTML;
        // The section endpoint always wraps output in a (possibly empty)
        // <div id="shopify-section-...">, so holder.children.length is never 0 even with
        // no recommendations — check for an actual product card instead.
        setRoutineWrapVisible(holder, !!holder.querySelector('.cart-drawer-v2-routine-item'));
      })
      .catch(function () {
        holder.innerHTML = '';
        setRoutineWrapVisible(holder, false);
      })
      .finally(function () {
        holder.dataset.v2Loading = 'false';
      });
  }

  function initRecommendations() {
    document.querySelectorAll('[data-v2-routine-holder][data-routine-mode="recommendations"]').forEach(function (holder) {
      if (!holder.children.length) loadRecommendations(holder);
    });
  }

  // #cartItemsHolder gets its innerHTML wholesale-replaced by theme.dev.js's build()
  // (add-to-cart / quantity change) and by cart-drawer-scripts.liquid's refreshCartDrawer()
  // (line-item remove) on every AJAX cart mutation. [data-v2-extras-holder] (routine +
  // promo banner) lives as a sibling specifically to survive that, but its "hidden" class
  // and the header's item-count / recommendations still need to be kept in sync by hand
  // since neither of those flows knows this markup exists.
  // Shipping protection is a real cart line but never rendered as a visible row
  // (cart-line-items-new.liquid skips it), so a cart holding only that product must
  // still count as empty for the routine/promo extras block.
  function getEffectiveCartItemCount(cart) {
    var toggle = document.querySelector('[data-v2-shipping-protection-toggle]');
    var raw = toggle && toggle.getAttribute('data-variant-id');
    var spId = raw != null ? parseInt(raw, 10) : NaN;
    var count = (cart && cart.item_count) || 0;
    if (isNaN(spId) || !cart || !cart.items) return count;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      var vid = it.variant_id != null ? it.variant_id : it.id;
      if (Number(vid) === spId) count -= it.quantity || 0;
    }
    return count;
  }

  function refreshExtras() {
    trace('cartDrawerV2.refreshExtras', 'refresh header count + routine/promo extras (own /cart.js fetch, fires on theme:cart:added AND cart:updated)', {
      initiator: typeof window.sprklCurrentInitiator === 'function' ? window.sprklCurrentInitiator() : undefined,
    });
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var effectiveCount = getEffectiveCartItemCount(cart);
        var wrap = document.querySelector('[data-cart-items-qty-wrap]');
        if (wrap) wrap.textContent = ' (' + effectiveCount + ')';

        var extras = document.querySelector('[data-v2-extras-holder]');
        if (extras) extras.classList.toggle('hidden', effectiveCount === 0);
        updateExtrasVisibility();

        document.querySelectorAll('[data-v2-routine-holder][data-routine-mode="recommendations"]').forEach(function (holder) {
          holder.innerHTML = '';
          loadRecommendations(holder, cart);
        });
      })
      .catch(function () {});
  }

  // The routine holder lives inside <template data-cart-drawer-template> until
  // theme.dev.js's CartDrawer clones it into the live DOM, which happens after
  // DOMContentLoaded and fires 'theme:cart:loaded'. 'theme:cart:added' fires after every
  // add-to-cart / quantity change (theme.dev.js); 'cart:updated' fires after the custom
  // line-item remove flow (cart-drawer-scripts.liquid) — both need extras refreshed since
  // the last-in-cart product (and so the recommendations) can change either way.
  document.addEventListener('submit', function (e) {
    var checkoutBtn = e.target.querySelector && e.target.querySelector('.cart-drawer-v2-checkout');
    if (!checkoutBtn || !drawerRoot(checkoutBtn)) return;
    checkoutBtn.classList.add('is-loading');
  });

  // bfcache: if the user navigates back to the drawer instead of completing checkout,
  // the button DOM is restored mid-spin with no submit event to clear it.
  window.addEventListener('pageshow', function () {
    document.querySelectorAll('.cart-drawer-v2-checkout.is-loading').forEach(function (btn) {
      btn.classList.remove('is-loading');
    });
  });

  // refreshCartDrawer() (cart-drawer-scripts.liquid) replaces [data-items-holder]'s
  // innerHTML wholesale on every cart mutation. If a remove-undo timer is still
  // pending when that happens, the itemEl it points at gets detached and
  // finalizeRemoval() would later fire against stale/removed nodes. The DOM is
  // about to be replaced anyway (with fresh server state reflecting the removal),
  // so just drop the in-memory timers here - nothing to restore.
  document.addEventListener('sprkl:cart-drawer-items-will-replace', function () {
    undoTimers.forEach(function (entry) {
      window.clearTimeout(entry.timeoutId);
    });
    undoTimers.clear();
  });

  // A single add fires theme:cart:added plus several cart:updated (footer-repair refresh,
  // gift reconcile refresh, ...). Debounce so the burst collapses into ONE /cart.js +
  // recommendations rebuild instead of one per event.
  var refreshExtrasTimer = null;
  function scheduleRefreshExtras() {
    clearTimeout(refreshExtrasTimer);
    refreshExtrasTimer = setTimeout(refreshExtras, 200);
  }
  document.addEventListener('theme:cart:loaded', initRecommendations);
  document.addEventListener('theme:cart:added', scheduleRefreshExtras);
  document.addEventListener('cart:updated', scheduleRefreshExtras);

  // theme.dev.js's add-to-cart flow (button.js -> addToCart() -> getCart() -> build()) always
  // repaints [data-cart-price-holder] from the legacy api-cart-items section's plain markup,
  // which has no v2 savings/gift/shipping-protection classes - same corruption the shipping
  // protection toggle and quantity buttons had. Repair it right after. skipLoadingShell: build()
  // already populated [data-items-holder] with correct v2 item markup and opened the drawer, so
  // there's nothing to skeleton-flash here - just needs the footer fixed underneath.
  document.addEventListener('theme:cart:added', function () {
    trace('on(theme:cart:added)', 'theme.dev.js finished add-to-cart / qty change -> repair v2 footer via full refresh', {});
    if (typeof window.sprklRefreshCartDrawer === 'function') {
      withInit('theme:cart:added', 'on(theme:cart:added)', 'footer repair refresh after add-to-cart', function () {
        return window.sprklRefreshCartDrawer(null, { skipLoadingShell: true, reason: 'theme:cart:added' });
      });
    }
  });
})();
